import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { Alert, DatePicker, Input, Modal, Select, Spin } from 'antd';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import ContentLayout from '../../layout/ContentLayout';
import styles from '../../styles/webshopAndProducts.module.scss';
import GenericTable from '../../components/tables/GenericTable';
import Pagination from '../../components/layout/Pagination';
import Spinner from '../../components/elements/Spinner/Spinner';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import { fetchAuditLogs, fetchAuditEntityTypes } from '../../api/audit-log-api';
import { formatValue } from '../../utils/formatValue';

const { RangePicker } = DatePicker;

const PAGE_SIZE = 20;

const pillStyle = {
  padding: '4px 12px',
  borderRadius: '16px',
  fontSize: '14px',
  fontWeight: '500',
  display: 'inline-block',
};

const OPS = {
  create: { label: 'Kreiranje', backgroundColor: '#ECFDF3', color: '#027A48' },
  update: { label: 'Izmena', backgroundColor: '#EFF8FF', color: '#175CD3' },
  delete: { label: 'Brisanje', backgroundColor: '#FEF3F2', color: '#B42318' },
};

const METHOD_OPTIONS = [
  { value: 'POST', label: 'POST (kreiranje / akcija)' },
  { value: 'PUT', label: 'PUT (izmena)' },
  { value: 'PATCH', label: 'PATCH (izmena)' },
  { value: 'DELETE', label: 'DELETE (brisanje)' },
];

const formatOpAsPill = (op) => {
  const meta = OPS[op];
  if (!meta) return null;
  const { label, ...colors } = meta;
  return <span style={{ ...pillStyle, ...colors }}>{label}</span>;
};

// Global table styles force nowrap + ellipsis on every cell; diff values need to wrap instead.
const wrapStyle = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  minWidth: 160,
  maxWidth: 520,
};

const fieldColumns = [
  {
    key: 'path',
    label: 'Polje',
    render: (val) => (
      <div style={{ ...wrapStyle, minWidth: 120, maxWidth: 260 }}>
        <code>{val}</code>
      </div>
    ),
  },
  { key: 'before', label: 'Pre', render: (val) => formatValue(val, wrapStyle) },
  { key: 'after', label: 'Posle', render: (val) => formatValue(val, wrapStyle) },
];

const ChangesModal = ({ entry, onClose }) => (
  <Modal
    open={!!entry}
    onCancel={onClose}
    footer={null}
    width="min(1280px, 95vw)"
    centered
    title={
      <div style={{ whiteSpace: 'normal', paddingRight: 24 }}>
        {entry?.summary}
      </div>
    }
    styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
  >
    {entry && (
      <>
        <p style={{ color: '#475467', wordBreak: 'break-word' }}>
          {dayjs(entry.createdAt).format('DD.MM.YYYY. HH:mm:ss')} ·{' '}
          {entry.actor?.name} ({entry.actor?.email}) ·{' '}
          <code>
            {entry.method} {entry.path}
          </code>
        </p>
        {entry.changes.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="Akcija nije menjala podatke u bazi (npr. slanje notifikacije)."
          />
        )}
        {entry.changes.map((change, index) => (
          <div
            key={`${change.model}-${change.docId}-${index}`}
            style={{ marginBottom: 24 }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              {formatOpAsPill(change.op)}
              <strong>{change.label}</strong>
              {change.displayName && <span>{change.displayName}</span>}
              {index === 0 && entry.changes.length > 1 && (
                <span style={{ color: '#98A2B3' }}>(glavna izmena)</span>
              )}
            </div>
            {change.fields.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <GenericTable
                  data={change.fields.map((f) => ({ ...f, _id: f.path }))}
                  columns={fieldColumns}
                  grayTheme
                />
              </div>
            ) : (
              change.op === 'update' && (
                <span style={{ color: '#98A2B3' }}>Bez detalja polja.</span>
              )
            )}
          </div>
        ))}
        {entry.truncated && (
          <Alert
            type="warning"
            showIcon
            message="Prikazano je prvih 50 izmena; akcija je promenila više zapisa."
          />
        )}
      </>
    )}
  </Modal>
);

ChangesModal.propTypes = {
  entry: PropTypes.shape({
    summary: PropTypes.string,
    createdAt: PropTypes.string,
    actor: PropTypes.shape({ name: PropTypes.string, email: PropTypes.string }),
    method: PropTypes.string,
    path: PropTypes.string,
    changes: PropTypes.arrayOf(PropTypes.object),
    truncated: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
};

const AuditLogPage = () => {
  const { can, loading: permLoading } = usePermissions();
  const canRead = can('audit:read');
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [entityType, setEntityType] = useState(null);
  const [method, setMethod] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [entityTypes, setEntityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('page', String(currentPage));
        return next;
      },
      { replace: true }
    );
  }, [currentPage, setSearchParams]);

  useEffect(() => {
    if (permLoading || !canRead) return;
    fetchAuditEntityTypes().then(({ data }) => setEntityTypes(data || []));
  }, [permLoading, canRead]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await fetchAuditLogs({
      page: currentPage,
      limit: PAGE_SIZE,
      search: debouncedSearch.trim(),
      entityType: entityType || undefined,
      method: method || undefined,
      from: dateRange?.[0]
        ? dateRange[0].startOf('day').toISOString()
        : undefined,
      to: dateRange?.[1] ? dateRange[1].endOf('day').toISOString() : undefined,
    });
    if (error) {
      toast.error(error);
    } else {
      setEntries(data.items.map((item) => ({ ...item, _id: item.id })));
      setTotalPages(data.totalPages || 1);
    }
    setLoading(false);
  }, [currentPage, debouncedSearch, entityType, method, dateRange]);

  useEffect(() => {
    if (!permLoading && canRead) load();
  }, [permLoading, canRead, load]);

  const resetPage = () => setCurrentPage(1);

  const columns = [
    {
      key: 'createdAt',
      label: 'Vreme',
      render: (val) => dayjs(val).format('DD.MM.YYYY. HH:mm'),
    },
    {
      key: 'actor',
      label: 'Administrator',
      render: (actor) => (
        <div>
          <div>{actor?.name || '—'}</div>
          <div style={{ color: '#667085', fontSize: 12 }}>{actor?.email}</div>
        </div>
      ),
    },
    { key: 'summary', label: 'Akcija' },
    {
      key: 'changes',
      label: 'Izmenjena polja',
      render: (changes) => {
        const count = (changes || []).reduce(
          (sum, c) => sum + (c.fields?.length || 0),
          0
        );
        return count > 0 ? count : '—';
      },
    },
  ];

  if (permLoading) return <Spin />;
  if (!canRead) {
    return (
      <ContentLayout pt={true}>
        <Alert
          type="warning"
          showIcon
          message="Nemate dozvolu za pregled istorije akcija."
        />
      </ContentLayout>
    );
  }

  return (
    <>
      <ContentLayout pt={true}>
        <div className={styles.webshop__orders}>
          <div
            className={styles.header}
            style={{ flexDirection: 'column', alignItems: 'stretch', gap: 16 }}
          >
            <h2 style={{ margin: 0 }}>Istorija akcija</h2>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <Input
                placeholder="Pretraži po akciji, administratoru, entitetu..."
                allowClear
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  resetPage();
                }}
                style={{ flex: '1 1 280px', minWidth: 220 }}
              />
              <Select
                placeholder="Tip entiteta"
                allowClear
                value={entityType}
                options={entityTypes}
                onChange={(val) => {
                  setEntityType(val ?? null);
                  resetPage();
                }}
                style={{ flex: '0 1 200px', minWidth: 160 }}
              />
              <Select
                placeholder="Tip zahteva"
                allowClear
                value={method}
                options={METHOD_OPTIONS}
                popupMatchSelectWidth={false}
                onChange={(val) => {
                  setMethod(val ?? null);
                  resetPage();
                }}
                style={{ flex: '0 1 180px', minWidth: 150 }}
              />
              <RangePicker
                format="DD.MM.YYYY."
                placeholder={['Od', 'Do']}
                value={dateRange}
                onChange={(val) => {
                  setDateRange(val);
                  resetPage();
                }}
                style={{ flex: '0 1 260px', minWidth: 230 }}
              />
            </div>
          </div>
          <div className={`${styles.body} products_wrapper`}>
            {loading ? (
              <Spinner size="lg" />
            ) : (
              <>
                <GenericTable
                  data={entries}
                  columns={columns}
                  actions={[
                    {
                      key: 'view',
                      label: 'Vidi izmene',
                      icon: (
                        <img src="/assets/icons/view-icon.svg" alt="View" />
                      ),
                      onClick: (entry) => setSelectedEntry(entry),
                    },
                  ]}
                  emptyState={{
                    icon: '/assets/icons/no-ads.svg',
                    title: 'Trenutno nema zabeleženih akcija',
                  }}
                />
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </div>
        </div>
      </ContentLayout>
      <ChangesModal
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />
    </>
  );
};

export default AuditLogPage;
